import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AdminTenantSelector, { type AdminTenantOption } from '../components/AdminTenantSelector';
import { PortalDataState } from '../components/PortalDataState';
import { PortalPageHeader } from '../components/PortalPageHeader';
import { PortalStatusPill } from '../components/PortalStatusPill';
import { Notice } from '../components/status';
import { usePortalContext } from '../context/PortalContext';
import {
  partnerStatusPresentation,
  shareStatusPresentation
} from '../services/collaboration/supplierCollaborationVocabulary';
import {
  assignCollaborationContact,
  createCollaborationContact,
  deleteCollaborationPartner,
  deleteCollaborationPartnerTemplateAuthorisation,
  deleteCollaborationPartnerTemplateReplyTo,
  issueCollaborationPartnerAdminInvite,
  loadCollaborationInstancesForTenant,
  loadCollaborationKnownTemplateKeys,
  loadCollaborationPartnerProfile,
  loadCollaborationPolicyForTenant,
  loadCollaborationPrincipalContactChanges,
  loadCollaborationTenants,
  reviewCollaborationPrincipalContactChangeRequest,
  saveCollaborationPartnerTemplateAuthorisation,
  saveCollaborationPartnerTemplateReplyTo,
  updateCollaborationContact,
  updateCollaborationPartner
} from './collaborationRouteApi';
import type {
  CollaborationContact,
  CollaborationContactFormState,
  CollaborationInstance,
  CollaborationPartnerProfile,
  CollaborationPartnerFootprintMode,
  CollaborationPartnerRiskTier,
  CollaborationPartnerTemplateAuthorisationFormState,
  CollaborationPartnerTemplateReplyToFormState,
  CollaborationPolicy,
  CollaborationPrincipalContactChangeRequest
} from './collaborationControlPlaneShared';
import { collaborationPartnerRiskTierOptions } from './collaborationControlPlaneShared';
import { deriveSupplierProfileReadiness, type SupplierProfileNextAction } from './supplierProfileLogic';

interface IssuedInvite {
  inviteUrl: string;
  invitedEmail: string;
  expiresAt: string;
}

const displayDate = (value: string | null | undefined): string => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const daysSince = (value: string, now = new Date()): number | null => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.max(0, Math.floor((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000)));
};

const CollaborationPartnerProfilePage = (): JSX.Element => {
  const { partnerId = '' } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const { tenant, loading: portalLoading } = usePortalContext();
  const canManage = tenant?.capabilities?.canManageSupplierCollaboration === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTenantId = searchParams.get('tenantId') || '';

  const [tenants, setTenants] = useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState(requestedTenantId);
  const [profile, setProfile] = useState<CollaborationPartnerProfile | null>(null);
  const [policy, setPolicy] = useState<CollaborationPolicy | null>(null);
  const [knownTemplateKeys, setKnownTemplateKeys] = useState<string[]>([]);
  const [instances, setInstances] = useState<CollaborationInstance[]>([]);
  const [principalChanges, setPrincipalChanges] = useState<CollaborationPrincipalContactChangeRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState('');
  const [issuedInvite, setIssuedInvite] = useState<IssuedInvite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [supportWarning, setSupportWarning] = useState<string | null>(null);

  const [contactForm, setContactForm] = useState<CollaborationContactFormState>({
    partnerId,
    email: '',
    displayName: '',
    contactType: 'person',
    isPrincipalContact: false
  });
  const [replyToDraft, setReplyToDraft] = useState('');
  const [supplierDraft, setSupplierDraft] = useState({
    displayName: '',
    primaryDomain: '',
    externalSupplierReference: '',
    footprintMode: 'portal_only' as CollaborationPartnerFootprintMode,
    supplierCategory: '',
    riskTier: '' as CollaborationPartnerRiskTier | ''
  });
  const [templateReplyForm, setTemplateReplyForm] = useState<CollaborationPartnerTemplateReplyToFormState>({
    partnerId,
    templateKey: '',
    replyToEmail: ''
  });
  const [templateAccessForm, setTemplateAccessForm] = useState<CollaborationPartnerTemplateAuthorisationFormState>({
    partnerId,
    templateKey: '',
    contactId: ''
  });
  const [assignmentContactId, setAssignmentContactId] = useState('');
  const [assignmentInstanceId, setAssignmentInstanceId] = useState('');

  useEffect(() => {
    if (portalLoading || !canManage) return;
    let cancelled = false;
    void loadCollaborationTenants()
      .then((payload) => {
        if (cancelled) return;
        setTenants(payload.tenants);
        const resolvedTenantId = payload.tenants.some((entry) => entry.tenantId === requestedTenantId)
          ? requestedTenantId
          : payload.defaultTenantId;
        setSelectedTenantId(resolvedTenantId);
        if (resolvedTenantId && resolvedTenantId !== requestedTenantId) {
          const next = new URLSearchParams(searchParams);
          next.set('tenantId', resolvedTenantId);
          setSearchParams(next, { replace: true });
        }
      })
      .catch((loadError: Error) => {
        if (!cancelled) setError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, portalLoading, requestedTenantId, searchParams, setSearchParams]);

  const loadProfile = useCallback(async (): Promise<void> => {
    if (!canManage || !selectedTenantId || !partnerId) return;
    setLoading(true);
    setError(null);
    setSupportWarning(null);
    try {
      const [profilePayload, policyPayload] = await Promise.all([
        loadCollaborationPartnerProfile({ tenantId: selectedTenantId, partnerId }),
        loadCollaborationPolicyForTenant(selectedTenantId)
      ]);
      if (!policyPayload) {
        throw new Error('Supplier recipient policy is unavailable, so readiness cannot be shown safely.');
      }
      setProfile(profilePayload);
      setPolicy(policyPayload);
      setReplyToDraft(profilePayload.partner.defaultReplyToEmail || '');
      setSupplierDraft({
        displayName: profilePayload.partner.displayName,
        primaryDomain: profilePayload.partner.primaryDomain || '',
        externalSupplierReference: profilePayload.partner.externalSupplierReference || '',
        footprintMode: profilePayload.partner.footprintMode,
        supplierCategory: profilePayload.partner.supplierCategory || '',
        riskTier: profilePayload.partner.riskTier || ''
      });
      setContactForm((current) => ({ ...current, partnerId }));
      setTemplateReplyForm((current) => ({ ...current, partnerId }));
      setTemplateAccessForm((current) => ({ ...current, partnerId }));

      const supportResults = await Promise.allSettled([
        loadCollaborationKnownTemplateKeys(selectedTenantId),
        loadCollaborationInstancesForTenant(selectedTenantId),
        loadCollaborationPrincipalContactChanges({ tenantId: selectedTenantId, partnerId })
      ]);
      setKnownTemplateKeys(supportResults[0].status === 'fulfilled' ? supportResults[0].value : []);
      setInstances(supportResults[1].status === 'fulfilled' ? supportResults[1].value : []);
      setPrincipalChanges(supportResults[2].status === 'fulfilled' ? supportResults[2].value : []);
      if (supportResults.some((result) => result.status === 'rejected')) {
        setSupportWarning('Some advanced setup information could not be loaded. Core supplier readiness remains available.');
      }
    } catch (loadError) {
      setProfile(null);
      setPolicy(null);
      setError((loadError as Error).message);
    } finally {
      setLoading(false);
    }
  }, [canManage, partnerId, selectedTenantId]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const activePrincipal = useMemo(
    () =>
      profile?.contacts
        .filter(
          (contact) =>
            contact.status === 'active' && contact.contactType === 'person' && contact.isPrincipalContact
        )
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0] ?? null,
    [profile]
  );
  const readiness = useMemo(
    () => (profile && policy ? deriveSupplierProfileReadiness(profile, policy) : null),
    [policy, profile]
  );
  const latestShare = useMemo(
    () => profile?.recentShares.slice().sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))[0] ?? null,
    [profile]
  );
  const lastExchangeDays = latestShare ? daysSince(latestShare.updatedAt) : null;
  const activeInstances = instances.filter((instance) => instance.isActive && instance.acceptsCollaboration);
  const pendingPrincipalChanges = principalChanges.filter((request) => request.status === 'pending');

  const canIssueInvite = Boolean(
    profile &&
      activePrincipal &&
      ['draft', 'active', 'awaiting_supplier_registration', 'declined'].includes(profile.partner.status)
  );

  const runAction = async (key: string, action: () => Promise<void>, success: string): Promise<void> => {
    setSavingAction(key);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
      await loadProfile();
    } catch (actionError) {
      setError((actionError as Error).message);
    } finally {
      setSavingAction('');
    }
  };

  const addContact = async (): Promise<void> => {
    if (!contactForm.email.trim()) {
      setError('Enter the contact email address.');
      return;
    }
    if (contactForm.contactType === 'shared_mailbox' && contactForm.isPrincipalContact) {
      setError('Only a named person can be the principal contact.');
      return;
    }
    await runAction(
      'add-contact',
      async () => {
        await createCollaborationContact({ tenantId: selectedTenantId, contactForm });
        setContactForm({
          partnerId,
          email: '',
          displayName: '',
          contactType: 'person',
          isPrincipalContact: false
        });
      },
      'Contact saved.'
    );
  };

  const setContactStatus = async (contact: CollaborationContact): Promise<void> => {
    const status = contact.status === 'active' ? 'disabled' : 'active';
    await runAction(
      `contact-${contact.contactId}`,
      () => updateCollaborationContact({ tenantId: selectedTenantId, contact, status }),
      `Contact ${status === 'active' ? 'enabled' : 'disabled'}.`
    );
  };

  const issueInvite = async (): Promise<void> => {
    if (!profile || !canIssueInvite) return;
    setSavingAction('issue-invite');
    setError(null);
    setNotice(null);
    try {
      const result = await issueCollaborationPartnerAdminInvite({
        tenantId: selectedTenantId,
        partnerId: profile.partner.partnerId
      });
      setIssuedInvite(result);
      setNotice('A fresh administrator link was created. Edinshall does not email it automatically.');
      await loadProfile();
    } catch (inviteError) {
      setError((inviteError as Error).message);
    } finally {
      setSavingAction('');
    }
  };

  const copyInvite = async (): Promise<void> => {
    if (!issuedInvite) return;
    try {
      await navigator.clipboard.writeText(issuedInvite.inviteUrl);
      setNotice('Administrator link copied.');
    } catch {
      setError('The link could not be copied automatically. Select and copy it below.');
    }
  };

  const setPartnerStatus = async (status: 'active' | 'suspended' | 'deactivated'): Promise<void> => {
    if (!profile) return;
    await runAction(
      `status-${status}`,
      () => updateCollaborationPartner({ tenantId: selectedTenantId, partner: profile.partner, status }),
      `Supplier ${status === 'active' ? 'activated' : status}.`
    );
  };

  const saveDefaultReplyTo = async (): Promise<void> => {
    if (!profile) return;
    await runAction(
      'default-reply',
      () =>
        updateCollaborationPartner({
          tenantId: selectedTenantId,
          partner: { ...profile.partner, defaultReplyToEmail: replyToDraft.trim() || null },
          status: profile.partner.status
        }),
      'Default reply-to updated.'
    );
  };

  const saveSupplierDetails = async (): Promise<void> => {
    if (!profile || !supplierDraft.displayName.trim()) {
      setError('Enter the supplier name.');
      return;
    }
    await runAction(
      'supplier-details',
      () =>
        updateCollaborationPartner({
          tenantId: selectedTenantId,
          partner: {
            ...profile.partner,
            displayName: supplierDraft.displayName.trim(),
            primaryDomain: supplierDraft.primaryDomain.trim() || null,
            externalSupplierReference: supplierDraft.externalSupplierReference.trim() || null,
            footprintMode: supplierDraft.footprintMode,
            supplierCategory: supplierDraft.supplierCategory.trim() || null,
            riskTier: supplierDraft.riskTier || null
          },
          status: profile.partner.status
        }),
      'Supplier details updated.'
    );
  };

  const saveTemplateReplyTo = async (): Promise<void> => {
    if (!templateReplyForm.templateKey || !templateReplyForm.replyToEmail.trim()) {
      setError('Select a template and enter a reply-to email.');
      return;
    }
    await runAction(
      'template-reply',
      async () => {
        await saveCollaborationPartnerTemplateReplyTo({ tenantId: selectedTenantId, form: templateReplyForm });
        setTemplateReplyForm({ partnerId, templateKey: '', replyToEmail: '' });
      },
      'Template reply-to saved.'
    );
  };

  const saveTemplateAccess = async (): Promise<void> => {
    if (!templateAccessForm.templateKey) {
      setError('Select a template before adding access.');
      return;
    }
    await runAction(
      'template-access',
      async () => {
        await saveCollaborationPartnerTemplateAuthorisation({
          tenantId: selectedTenantId,
          form: templateAccessForm
        });
        setTemplateAccessForm({ partnerId, templateKey: '', contactId: '' });
      },
      'Supplier work access updated.'
    );
  };

  const reviewPrincipalChange = async (
    request: CollaborationPrincipalContactChangeRequest,
    action: 'approve' | 'reject'
  ): Promise<void> => {
    await runAction(
      `principal-${request.principalContactChangeRequestId}`,
      () =>
        reviewCollaborationPrincipalContactChangeRequest({
          tenantId: selectedTenantId,
          requestId: request.principalContactChangeRequestId,
          action
        }),
      `Principal-contact request ${action === 'approve' ? 'approved' : 'rejected'}.`
    );
  };

  const assignContact = async (): Promise<void> => {
    if (!assignmentContactId || !assignmentInstanceId) {
      setError('Select a contact and an installation.');
      return;
    }
    await runAction(
      'assign-contact',
      () =>
        assignCollaborationContact({
          tenantId: selectedTenantId,
          contactId: assignmentContactId,
          instanceId: assignmentInstanceId
        }),
      'Contact assigned to the installation.'
    );
  };

  const removeSupplier = async (): Promise<void> => {
    if (!profile) return;
    const confirmed = window.confirm(
      `Permanently remove ${profile.partner.displayName}? Only unused supplier setups can be removed. Suppliers with registration or collaboration history must be deactivated instead.`
    );
    if (!confirmed) return;
    setSavingAction('remove');
    setError(null);
    try {
      await deleteCollaborationPartner({ tenantId: selectedTenantId, partnerId: profile.partner.partnerId });
      navigate(`/collaboration?tab=partners&tenantId=${encodeURIComponent(selectedTenantId)}`, { replace: true });
    } catch (removeError) {
      setError((removeError as Error).message);
      setSavingAction('');
    }
  };

  const changeTenant = (tenantId: string): void => {
    navigate(`/collaboration?tab=partners&tenantId=${encodeURIComponent(tenantId)}`);
  };

  const focusElement = (id: string): void => {
    window.requestAnimationFrame(() => document.getElementById(id)?.focus());
  };

  const performNextAction = (action: SupplierProfileNextAction): void => {
    switch (action) {
      case 'add-principal-contact':
        setContactForm((current) => ({ ...current, contactType: 'person', isPrincipalContact: true }));
        focusElement('supplier-contact-email');
        break;
      case 'issue-admin-invite':
        void issueInvite();
        break;
      case 'activate-supplier':
        void setPartnerStatus('active');
        break;
      case 'configure-reply-to':
        focusElement('supplier-default-reply-to');
        break;
      case 'authorise-template':
        focusElement('supplier-template-access');
        break;
      default:
        break;
    }
  };

  const nextActionLabel = (action: SupplierProfileNextAction): string => {
    switch (action) {
      case 'add-principal-contact': return 'Add principal contact';
      case 'issue-admin-invite': return 'Issue administrator link';
      case 'activate-supplier': return 'Activate supplier';
      case 'configure-reply-to': return 'Configure reply-to';
      case 'authorise-template': return 'Add work access';
      default: return 'Setup complete';
    }
  };

  if (portalLoading) {
    return <PortalDataState kind="loading" heading="Checking supplier-collaboration access" message="Confirming your management permissions." />;
  }
  if (!canManage) {
    return (
      <PortalDataState
        kind="restricted"
        heading="Supplier Profile is not available"
        message="Ask a Supplier Quality Collaboration manager to review or update this supplier."
      />
    );
  }

  const profileStatus = profile ? partnerStatusPresentation(profile.partner.status) : null;

  return (
    <div>
      <Link to={`/collaboration?tab=partners&tenantId=${encodeURIComponent(selectedTenantId)}`}>
        ← Back to Suppliers
      </Link>
      <div style={{ marginTop: '12px' }}>
        <PortalPageHeader
          title={profile?.partner.displayName || 'Supplier Profile'}
          description="One place to complete supplier setup, maintain access and review recent collaboration."
          actions={(
            <button className="btn secondary" type="button" onClick={() => void loadProfile()} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          )}
          helpHref="/docs?path=docs/documentation/users/guide-supplier-collaboration-journey.md"
          helpLabel="Supplier setup guide"
          context={(
            <div className="card">
              <AdminTenantSelector
                label="Customer tenant"
                value={selectedTenantId}
                options={tenants}
                onChange={changeTenant}
                disabled={loading || Boolean(savingAction)}
                helperText="This Supplier Profile and every action on it belong to the selected customer tenant."
              />
            </div>
          )}
        />
      </div>

      {error && <Notice kind="danger" marginTop={10}>{error}</Notice>}
      {notice && <Notice kind="success" marginTop={10}>{notice}</Notice>}
      {supportWarning && <Notice kind="warning" marginTop={10}>{supportWarning}</Notice>}

      {loading && !profile && (
        <PortalDataState kind="loading" heading="Loading Supplier Profile" message="Checking setup, access and recent work." />
      )}
      {!loading && !profile && error && (
        <PortalDataState
          kind="failed"
          heading="Supplier Profile could not be loaded"
          message="The page has not guessed at supplier readiness. Retry, or return to the supplier list."
          action={<button className="btn secondary" type="button" onClick={() => void loadProfile()}>Retry</button>}
        />
      )}

      {profile && policy && readiness && (
        <div className="collaboration-profile-stack">
          <section className="card">
            <div className="collaboration-profile-heading">
              <div>
                <h3>Setup and readiness</h3>
                <p className="muted">
                  Activation and send-time requirements are shown separately so one is never mistaken for the other.
                </p>
              </div>
              {profileStatus && <PortalStatusPill label={profileStatus.label} tone={profileStatus.tone} />}
            </div>

            <h4>Set up and activate</h4>
            <ol className="collaboration-readiness-list">
              {readiness.activationSteps.map((step) => (
                <li className="collaboration-readiness-step" key={step.id}>
                  <PortalStatusPill
                    label={
                      step.complete
                        ? 'Complete'
                        : step.waiting
                          ? 'Waiting'
                          : readiness.nextActivationStepId === step.id
                            ? 'Next step'
                            : 'Pending'
                    }
                    tone={
                      step.complete
                        ? 'success'
                        : readiness.nextActivationStepId === step.id
                          ? 'warning'
                          : 'neutral'
                    }
                  />
                  <span><strong>{step.label}</strong><p className="muted">{step.detail}</p></span>
                </li>
              ))}
            </ol>

            {readiness.nextAction && (
              <div className="collaboration-card-actions">
                <button
                  className="btn"
                  type="button"
                  onClick={() => performNextAction(readiness.nextAction)}
                  disabled={Boolean(savingAction)}
                >
                  {savingAction ? 'Working…' : nextActionLabel(readiness.nextAction)}
                </button>
              </div>
            )}

            <h4>Ready to send work</h4>
            <ol className="collaboration-readiness-list">
              {readiness.sendingSteps.map((step) => (
                <li className="collaboration-readiness-step" key={step.id}>
                  <PortalStatusPill label={step.complete ? 'Ready' : 'Required before sending'} tone={step.complete ? 'success' : 'warning'} />
                  <span><strong>{step.label}</strong><p className="muted">{step.detail}</p></span>
                </li>
              ))}
            </ol>
          </section>

          <details className="card collaboration-disclosure">
            <summary>Preview the supplier’s steps</summary>
            <ol>
              <li>You create an administrator link and send it to the named supplier contact.</li>
              <li>The supplier opens the link and signs in with their Microsoft account.</li>
              <li>They register their organisation and accept the current Supplier Collaboration Terms.</li>
              <li>They accept the customer relationship and use the Supplier Quality Workspace for shared work.</li>
            </ol>
            <p className="muted">Edinshall does not email administrator links automatically.</p>
          </details>

          <div className="collaboration-profile-grid">
            <section className="card">
              <h3>Supplier details</h3>
              <p className="muted">The organisation details your administrators and auditors use to recognise this supplier.</p>
              <div className="collaboration-form-grid">
                <label>
                  <span className="muted">Supplier name</span>
                  <input className="input" value={supplierDraft.displayName} onChange={(event) => setSupplierDraft({ ...supplierDraft, displayName: event.target.value })} required />
                </label>
                <label>
                  <span className="muted">Primary domain (optional)</span>
                  <input className="input" value={supplierDraft.primaryDomain} onChange={(event) => setSupplierDraft({ ...supplierDraft, primaryDomain: event.target.value })} />
                </label>
                <label>
                  <span className="muted">Your supplier reference (optional)</span>
                  <input className="input" value={supplierDraft.externalSupplierReference} onChange={(event) => setSupplierDraft({ ...supplierDraft, externalSupplierReference: event.target.value })} />
                </label>
                <label>
                  <span className="muted">Category (optional)</span>
                  <input className="input" value={supplierDraft.supplierCategory} maxLength={100} placeholder="e.g. Raw materials, Logistics" onChange={(event) => setSupplierDraft({ ...supplierDraft, supplierCategory: event.target.value })} />
                </label>
                <label>
                  <span className="muted">Risk tier (optional)</span>
                  <select className="input" value={supplierDraft.riskTier} onChange={(event) => setSupplierDraft({ ...supplierDraft, riskTier: event.target.value as CollaborationPartnerRiskTier | '' })}>
                    <option value="">Not set</option>
                    {collaborationPartnerRiskTierOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="muted">Collaboration footprint</span>
                  <select className="input" value={supplierDraft.footprintMode} onChange={(event) => setSupplierDraft({ ...supplierDraft, footprintMode: event.target.value as CollaborationPartnerFootprintMode })}>
                    <option value="portal_only">Supplier Quality Workspace</option>
                    <option value="collaboration_instance">Supplier also uses an Edinshall Audit installation</option>
                  </select>
                </label>
              </div>
              <div className="collaboration-card-actions">
                <button className="btn" type="button" onClick={() => void saveSupplierDetails()} disabled={Boolean(savingAction) || !supplierDraft.displayName.trim()}>
                  {savingAction === 'supplier-details' ? 'Saving…' : 'Save supplier details'}
                </button>
              </div>
            </section>

            <section className="card" id="supplier-contacts">
              <div className="collaboration-profile-heading">
                <div><h3>Contacts</h3><p className="muted">People and mailboxes used for this supplier relationship.</p></div>
                <PortalStatusPill
                  label={activePrincipal ? 'Principal ready' : 'Principal missing'}
                  tone={activePrincipal ? 'success' : 'warning'}
                />
              </div>
              <div className="collaboration-supplier-list">
                {profile.contacts.length === 0 && (
                  <PortalDataState kind="first-empty" heading="No contacts" message="Add the named person who will administer this supplier relationship." compact />
                )}
                {profile.contacts.map((contact) => (
                  <div className="list-row" key={contact.contactId} style={{ cursor: 'default' }}>
                    <span>
                      <strong>{contact.displayName || contact.emailNormalized}</strong>
                      <span className="muted collaboration-attention-detail">{contact.emailNormalized}</span>
                    </span>
                    <span className="collaboration-card-actions" style={{ marginTop: 0 }}>
                      {contact.isPrincipalContact && <PortalStatusPill label="Principal" tone="success" />}
                      <PortalStatusPill label={contact.status === 'active' ? 'Active' : 'Disabled'} tone={contact.status === 'active' ? 'success' : 'warning'} />
                      <button
                        className="btn secondary"
                        type="button"
                        onClick={() => void setContactStatus(contact)}
                        disabled={Boolean(savingAction)}
                      >
                        {contact.status === 'active' ? 'Disable' : 'Enable'}
                      </button>
                    </span>
                  </div>
                ))}
              </div>
              <h4>Add contact</h4>
              <div className="collaboration-form-grid">
                <label>
                  <span className="muted">Email</span>
                  <input id="supplier-contact-email" className="input" type="email" value={contactForm.email} onChange={(event) => setContactForm({ ...contactForm, email: event.target.value })} />
                </label>
                <label>
                  <span className="muted">Name (optional)</span>
                  <input className="input" value={contactForm.displayName} onChange={(event) => setContactForm({ ...contactForm, displayName: event.target.value })} />
                </label>
                <label>
                  <span className="muted">Contact type</span>
                  <select
                    className="input"
                    value={contactForm.contactType}
                    onChange={(event) =>
                      setContactForm({
                        ...contactForm,
                        contactType: event.target.value as CollaborationContactFormState['contactType'],
                        isPrincipalContact: event.target.value === 'shared_mailbox' ? false : contactForm.isPrincipalContact
                      })
                    }
                  >
                    <option value="person">Person</option>
                    <option value="shared_mailbox">Shared mailbox</option>
                  </select>
                </label>
                <label className="full-width">
                  <input
                    type="checkbox"
                    checked={contactForm.isPrincipalContact}
                    onChange={(event) => setContactForm({ ...contactForm, isPrincipalContact: event.target.checked })}
                    disabled={contactForm.contactType !== 'person'}
                  />{' '}Principal contact for governance and administrator registration
                </label>
              </div>
              <div className="collaboration-card-actions">
                <button className="btn" type="button" onClick={() => void addContact()} disabled={Boolean(savingAction) || !contactForm.email.trim()}>
                  {savingAction === 'add-contact' ? 'Adding…' : 'Add contact'}
                </button>
              </div>

              {pendingPrincipalChanges.length > 0 && (
                <>
                  <h4>Principal-contact requests</h4>
                  {pendingPrincipalChanges.map((request) => (
                    <div className="list-row" key={request.principalContactChangeRequestId} style={{ cursor: 'default' }}>
                      <span>
                        <strong>{request.proposedContactDisplayName || request.proposedContactEmailNormalized}</strong>
                        <span className="muted collaboration-attention-detail">Proposed replacement for {request.currentContactDisplayName || request.currentContactEmailNormalized}</span>
                      </span>
                      <span className="collaboration-card-actions" style={{ marginTop: 0 }}>
                        <button className="btn" type="button" onClick={() => void reviewPrincipalChange(request, 'approve')} disabled={Boolean(savingAction)}>Approve</button>
                        <button className="btn secondary" type="button" onClick={() => void reviewPrincipalChange(request, 'reject')} disabled={Boolean(savingAction)}>Reject</button>
                      </span>
                    </div>
                  ))}
                </>
              )}
            </section>

            <section className="card">
              <h3>Microsoft registration</h3>
              {profile.supplierOrganisation ? (
                <div className="collaboration-supplier-list">
                  <div><strong>{profile.supplierOrganisation.displayName}</strong></div>
                  <div className="muted">Domain: {profile.supplierOrganisation.primaryDomain || 'Not supplied'}</div>
                  <div className="muted">Active supplier administrators: {profile.supplierOrganisation.adminCount}</div>
                  <div className="muted">Last administrator sign-in: {displayDate(profile.supplierOrganisation.lastAdminSignInAt)}</div>
                  {profile.supplierOrganisation.termsAcceptance ? (
                    <div className="collaboration-quiet-success">
                      Latest recorded administrator acceptance: Supplier Collaboration Terms{' '}
                      <strong>{profile.supplierOrganisation.termsAcceptance.termsVersionId}</strong>, accepted{' '}
                      {displayDate(profile.supplierOrganisation.termsAcceptance.acceptedAt)}.
                    </div>
                  ) : (
                    <Notice kind="warning">No stored Terms acceptance evidence is available for an active administrator.</Notice>
                  )}
                </div>
              ) : (
                <PortalDataState kind="context-empty" heading="Registration not complete" message="Issue an administrator link after adding the named principal contact." compact />
              )}

              {issuedInvite && (
                <div className="card" style={{ marginTop: '12px', background: 'var(--panel-alt)' }}>
                  <strong>Send this link to {issuedInvite.invitedEmail}</strong>
                  <div className="muted">Expires {displayDate(issuedInvite.expiresAt)}</div>
                  <div style={{ overflowWrap: 'anywhere', marginTop: '6px' }}>{issuedInvite.inviteUrl}</div>
                  <div className="collaboration-card-actions">
                    <button className="btn secondary" type="button" onClick={() => void copyInvite()}>Copy link</button>
                    <a className="btn secondary" href={`mailto:${encodeURIComponent(issuedInvite.invitedEmail)}?body=${encodeURIComponent(issuedInvite.inviteUrl)}`}>Prepare email</a>
                  </div>
                </div>
              )}

              <h4>Administrator invitation history</h4>
              {profile.adminInvites.length === 0 ? (
                <PortalDataState kind="first-empty" heading="No administrator links issued" message="The next-step action above will create the first link when a principal contact is ready." compact />
              ) : (
                <div className="collaboration-supplier-list">
                  {profile.adminInvites.map((invite) => {
                    const expired = new Date(invite.expiresAt).getTime() < Date.now() && invite.status === 'pending';
                    return (
                      <div className="list-row" key={invite.adminInviteId} style={{ cursor: 'default' }}>
                        <span>
                          <strong>{invite.invitedEmailNormalized}</strong>
                          <span className="muted collaboration-attention-detail">Issued {displayDate(invite.createdAt)} · expires {displayDate(invite.expiresAt)}</span>
                        </span>
                        <PortalStatusPill label={expired ? 'Expired' : invite.status === 'redeemed' ? 'Accepted' : 'Waiting'} tone={invite.status === 'redeemed' ? 'success' : 'warning'} />
                      </div>
                    );
                  })}
                </div>
              )}
              {profile.adminInvites.length > 0 && canIssueInvite && (
                <div className="collaboration-card-actions">
                  <button className="btn secondary" type="button" onClick={() => void issueInvite()} disabled={Boolean(savingAction)}>
                    {savingAction === 'issue-invite' ? 'Issuing…' : 'Issue replacement link'}
                  </button>
                </div>
              )}
            </section>

            <section className="card" id="supplier-template-access-card">
              <h3>Work access</h3>
              <p className="muted">Choose which Audit templates this supplier may receive. This is checked again when work is sent.</p>
              {profile.templateAuthorisations.length === 0 ? (
                <PortalDataState kind="context-empty" heading="No templates authorised" message="The supplier cannot receive template-bound work until an access rule is added." compact />
              ) : (
                <div className="collaboration-supplier-list">
                  {profile.templateAuthorisations.map((entry) => {
                    const contact = profile.contacts.find((candidate) => candidate.contactId === entry.contactId);
                    return (
                      <div className="list-row" key={entry.partnerTemplateAuthorisationId} style={{ cursor: 'default' }}>
                        <span><strong>{entry.templateKey}</strong><span className="muted collaboration-attention-detail">{contact ? contact.displayName || contact.emailNormalized : 'Principal-contact route'}</span></span>
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => void runAction(
                            `delete-access-${entry.partnerTemplateAuthorisationId}`,
                            () => deleteCollaborationPartnerTemplateAuthorisation({ tenantId: selectedTenantId, authorisationId: entry.partnerTemplateAuthorisationId }),
                            'Supplier work access removed.'
                          )}
                          disabled={Boolean(savingAction)}
                        >Remove</button>
                      </div>
                    );
                  })}
                </div>
              )}
              {knownTemplateKeys.length === 0 ? (
                <PortalDataState kind="context-empty" heading="No published template suggestions" message="Publish or synchronise a template before adding a new supplier access rule." compact />
              ) : (
                <div className="collaboration-form-grid" style={{ marginTop: '12px' }}>
                  <label>
                    <span className="muted">Template</span>
                    <select id="supplier-template-access" className="input" value={templateAccessForm.templateKey} onChange={(event) => setTemplateAccessForm({ ...templateAccessForm, templateKey: event.target.value })}>
                      <option value="">Select template</option>
                      {knownTemplateKeys.map((key) => <option key={key} value={key}>{key}</option>)}
                    </select>
                  </label>
                  <label>
                    <span className="muted">Recipient route</span>
                    <select className="input" value={templateAccessForm.contactId} onChange={(event) => setTemplateAccessForm({ ...templateAccessForm, contactId: event.target.value })}>
                      <option value="">Principal contact</option>
                      {profile.contacts.filter((contact) => contact.status === 'active').map((contact) => (
                        <option key={contact.contactId} value={contact.contactId}>{contact.displayName || contact.emailNormalized}</option>
                      ))}
                    </select>
                  </label>
                  <div className="full-width"><button className="btn" type="button" onClick={() => void saveTemplateAccess()} disabled={Boolean(savingAction) || !templateAccessForm.templateKey}>Add work access</button></div>
                </div>
              )}
            </section>

            <section className="card">
              <h3>Reply routing</h3>
              <p className="muted">A reply-to is required when work is sent, but it never blocks supplier activation.</p>
              <label>
                <span className="muted">Supplier default reply-to</span>
                <input id="supplier-default-reply-to" className="input" type="email" value={replyToDraft} onChange={(event) => setReplyToDraft(event.target.value)} />
              </label>
              <div className="collaboration-card-actions">
                <button className="btn" type="button" onClick={() => void saveDefaultReplyTo()} disabled={Boolean(savingAction)}>Save default</button>
              </div>
              {profile.hasInstanceDefaultReplyTo && <p><PortalStatusPill label="Installation default available" tone="success" /></p>}

              <h4>Template-specific reply-to</h4>
              {profile.templateReplyTos.map((entry) => (
                <div className="list-row" key={entry.partnerTemplateReplyToId} style={{ cursor: 'default' }}>
                  <span><strong>{entry.templateKey}</strong><span className="muted collaboration-attention-detail">{entry.replyToEmail}</span></span>
                  <button
                    className="btn secondary"
                    type="button"
                    onClick={() => void runAction(
                      `delete-reply-${entry.partnerTemplateReplyToId}`,
                      () => deleteCollaborationPartnerTemplateReplyTo({ tenantId: selectedTenantId, overrideId: entry.partnerTemplateReplyToId }),
                      'Template reply-to removed.'
                    )}
                    disabled={Boolean(savingAction)}
                  >Remove</button>
                </div>
              ))}
              {knownTemplateKeys.length > 0 && (
                <div className="collaboration-form-grid" style={{ marginTop: '12px' }}>
                  <label>
                    <span className="muted">Template</span>
                    <select className="input" value={templateReplyForm.templateKey} onChange={(event) => setTemplateReplyForm({ ...templateReplyForm, templateKey: event.target.value })}>
                      <option value="">Select template</option>
                      {knownTemplateKeys.map((key) => <option key={key} value={key}>{key}</option>)}
                    </select>
                  </label>
                  <label>
                    <span className="muted">Reply-to email</span>
                    <input className="input" type="email" value={templateReplyForm.replyToEmail} onChange={(event) => setTemplateReplyForm({ ...templateReplyForm, replyToEmail: event.target.value })} />
                  </label>
                  <div className="full-width"><button className="btn secondary" type="button" onClick={() => void saveTemplateReplyTo()} disabled={Boolean(savingAction) || !templateReplyForm.templateKey || !templateReplyForm.replyToEmail.trim()}>Add template reply-to</button></div>
                </div>
              )}
            </section>

            <section className="card">
              <div className="collaboration-profile-heading">
                <div><h3>Recent collaboration</h3><p className="muted">The latest work exchanged with this supplier.</p></div>
                {latestShare && lastExchangeDays !== null && <PortalStatusPill label={`Last exchange ${lastExchangeDays}d ago`} tone={lastExchangeDays >= 90 ? 'warning' : 'success'} />}
              </div>
              {profile.recentShares.length === 0 ? (
                <PortalDataState kind="first-empty" heading="No work exchanged yet" message="This is normal for a newly activated supplier." compact />
              ) : (
                <div className="collaboration-supplier-list">
                  {profile.recentShares.map((share) => {
                    const status = shareStatusPresentation(share.status);
                    return (
                      <div className="list-row" key={share.shareInvitationId} style={{ cursor: 'default' }}>
                        <span><strong>{share.kind === 'self_assessment' ? 'Self-assessment questionnaire' : 'Finding'}</strong><span className="muted collaboration-attention-detail">Updated {displayDate(share.updatedAt)}</span></span>
                        <PortalStatusPill label={status.label} tone={status.tone} />
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <details className="card collaboration-disclosure">
            <summary>Advanced details and installation assignment</summary>
            <div className="collaboration-profile-grid">
              <div>
                <h4>Record details</h4>
                <div className="muted">Supplier ID: {profile.partner.partnerId}</div>
                <div className="muted">Updated {displayDate(profile.partner.updatedAt)} by {profile.partner.updatedBy || 'system'}</div>
              </div>
              <div>
                <h4>Contact-to-installation assignment</h4>
                <p className="muted">Optional. Use only when a particular supplier contact must receive work from a specific installation.</p>
                <div className="collaboration-form-grid">
                  <select className="input" aria-label="Contact" value={assignmentContactId} onChange={(event) => setAssignmentContactId(event.target.value)}>
                    <option value="">Select contact</option>
                    {profile.contacts.filter((contact) => contact.status === 'active').map((contact) => <option key={contact.contactId} value={contact.contactId}>{contact.displayName || contact.emailNormalized}</option>)}
                  </select>
                  <select className="input" aria-label="Installation" value={assignmentInstanceId} onChange={(event) => setAssignmentInstanceId(event.target.value)}>
                    <option value="">Select installation</option>
                    {activeInstances.map((instance) => <option key={instance.instanceId} value={instance.instanceId}>{instance.displayName || instance.siteUrl}</option>)}
                  </select>
                </div>
                <div className="collaboration-card-actions"><button className="btn secondary" type="button" onClick={() => void assignContact()} disabled={Boolean(savingAction) || !assignmentContactId || !assignmentInstanceId}>Assign contact</button></div>
              </div>
            </div>
          </details>

          <details className="card collaboration-disclosure collaboration-maintenance">
            <summary>Supplier lifecycle maintenance</summary>
            <p className="muted">These actions change or remove access. They are deliberately separated from ordinary setup and Save actions.</p>
            <div className="collaboration-card-actions">
              {profile.partner.status === 'active' && <button className="btn secondary" type="button" onClick={() => void setPartnerStatus('suspended')} disabled={Boolean(savingAction)}>Suspend supplier</button>}
              {profile.partner.status === 'suspended' && <button className="btn secondary" type="button" onClick={() => void setPartnerStatus('active')} disabled={Boolean(savingAction)}>Reactivate supplier</button>}
              {profile.partner.status !== 'deactivated' && profile.partner.status !== 'archived' && <button className="btn secondary" type="button" onClick={() => void setPartnerStatus('deactivated')} disabled={Boolean(savingAction)}>Deactivate supplier</button>}
              <button className="btn secondary" type="button" onClick={() => void removeSupplier()} disabled={Boolean(savingAction)}>Remove unused supplier</button>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default CollaborationPartnerProfilePage;
